import React, { useEffect, useContext, useState } from 'react'
import { ExtendableRadios } from '@zuto/molecules'
import { GridItem } from '@zuto/core/src/grid'
import { useCallbackOnce } from '@zuto/utilities'
import { fireReactGAEvent, trackMultipleInputChoice, determineRadioVariant, scrollToTheTop, questionFontStyle } from '../../utils'
import QuestionKey from '../../../../Questions/question-key'
import AppFormQuestion from '../../../AppFormQuestion'
import employmentStatusOptionsProminentColourScheme from './EmploymentStatusOptionsProminentColourScheme'
import { ApplicationFormContext } from '../../../ApplicationForm/ApplicationFormContext'
import withFade from '../../../HOC/withFade'
import NavigationButtons from '../../../NavigationButtons/NavigationButtons'
import EmploymentHistoryEntryContext from '../EmploymentEntry/EmploymentHistoryEntryContext'

import { HistoryIndex } from '../../HistoryEntry/HistoryEntry'
import { PrefillReviewPageApplicationFormContext } from '../../../ApplicationForm/PrefillReviewPageApplicationFormContext'
import { UpdateFollowingPrefillStepType } from '../../../../Questions/question-component-props'
import ToggleButtonGroup from '@zuto/atoms/src/Mui/ToogleButtonGroup/ToggleButtonGroup'
import { useAppFormEventsService } from '../../../../hooks/useAppFormEventsService'
import { AmplitudeEvents, trackAmplitudeEvent } from '../../../../utils/amplitude-utils'
import { toSnakeCase } from '../../../../utils/string-utils'

interface EmploymentStatusProps {
  entryId: HistoryIndex
  existingSituation?: string
  disablePrevious?: boolean
  updateFollowingPrefillStep?: UpdateFollowingPrefillStepType
}

const EmploymentStatus = ({ existingSituation, entryId, disablePrevious = false, updateFollowingPrefillStep }: EmploymentStatusProps) => {
  const appFormContext = useContext(ApplicationFormContext)
  const { featureSwitches } = appFormContext
  const eventsService = useAppFormEventsService()
  const { removeContinueCtaV1, reviewPageV3, questionCopyChangeV1, msmThemeV1 } = featureSwitches ?? {}
  const { hasNavigatedFromRequiredClick, setHasNavigatedFromRequiredClick } = useContext(PrefillReviewPageApplicationFormContext)
  const { dispatch } = useContext(EmploymentHistoryEntryContext)
  const { dispatch: appFormContextDispatch } = useContext(ApplicationFormContext)
  const [currentAnswer, setCurrentAnswer] = useState<string | undefined>(existingSituation ?? (!removeContinueCtaV1 ? 'full time permanent' : undefined))
  const [nextButtonCb] = useCallbackOnce()
  const [previousButtonCb] = useCallbackOnce()

  useEffect(() => {
    const label = `qst.employmentHistory.${parseInt(entryId, 10) - 1}.situation`
    fireReactGAEvent({
      category: 'Application Form',
      action: 'Question Visible',
      label,
      nonInteraction: true
    })
    eventsService?.sendEvent('AppFormQuestionVisible', { QuestionLabel: label })
    trackAmplitudeEvent(AmplitudeEvents.ApplicationQuestionViewed, { question_label: toSnakeCase(label) })
  }, [])

  useEffect(scrollToTheTop, [])

  const toggleChanged = (open: boolean) => {
    fireReactGAEvent({
      category: 'Application Form',
      action: open ? 'Accordion Open' : 'Accordion Closed',
      label: 'Employment Status'
    })
  }

  const extendedOptionSelected = (option: string) => {
    setCurrentAnswer(option)
    if (removeContinueCtaV1) {
      dispatch({
        type: 'UPDATE_STATUS',
        value: option
      })
      dispatch({
        type: 'NEXT'
      })
    }
  }

  const nextHandler = () => {
    if (currentAnswer !== undefined) {
      trackMultipleInputChoice({ label: `employmentSituation+${currentAnswer}` })
      dispatch({
        type: 'UPDATE_STATUS',
        value: currentAnswer
      })

      dispatch({
        type: 'NEXT'
      })
    }
  }

  const previousHandler = () => {
    if (currentAnswer !== undefined) {
      dispatch({
        type: 'UPDATE_STATUS',
        value: currentAnswer
      })
    }

    appFormContextDispatch({
      type: 'NAVIGATED_FROM_QUESTION',
      questionName: `employmentStatus-${parseInt(entryId, 10)}`,
      lastNavAction: 'PREVIOUS'
    })

    dispatch({
      type: 'PREVIOUS'
    })

    setTimeout(() => {
      if (updateFollowingPrefillStep) {
        if (reviewPageV3 && hasNavigatedFromRequiredClick) {
          updateFollowingPrefillStep(false, false, true)
        }
        if (entryId === '1' && !hasNavigatedFromRequiredClick) {
          updateFollowingPrefillStep(false, false, false)
        }
      }
    }, 50)

    if (reviewPageV3) {
      setHasNavigatedFromRequiredClick(false)
    }
  }

  const renderExtendedRadio = () => {
    if (msmThemeV1) {
      return (
        <ToggleButtonGroup
          id="employment-status"
          options={employmentStatusOptionsProminentColourScheme}
          title="Show more options"
          extendable={true}
          defaultState={currentAnswer}
          valueChanged={extendedOptionSelected}
        />
      )
    }
    return (
      <ExtendableRadios
        toggleChanged={toggleChanged}
        options={employmentStatusOptionsProminentColourScheme}
        id="employment-status"
        title="Show more options"
        valueChanged={extendedOptionSelected}
        defaultState={currentAnswer}
        checkedVariant="fill"
        mb={5}
        mt={3}
        variant={determineRadioVariant({
          smallRadiosSideBySide: false
        })}
        removeContinueCta={removeContinueCtaV1}
        prominentColourScheme={true}
      />
    )
  }

  const renderQuestionTitle = () => {
    const notFirstEntry = entryId !== '1'
    if (questionCopyChangeV1) {
      if (notFirstEntry) return 'What was your previous employment status?'
      return "What's your employment status?"
    }

    return `Can you tell us about your ${notFirstEntry ? 'previous ' : ''}employment?`
  }

  return (
    <>
      <GridItem
        gridColumn={['auto / span 12', '3 / span 8']}
        minHeight="174px"
      >
        <AppFormQuestion
          questionKey={`${QuestionKey.EMPLOYMENT_HISTORY}.situation`}
          mt={3}
          textStyle={questionFontStyle}
        >
          {renderQuestionTitle()}
        </AppFormQuestion>
        {renderExtendedRadio()}
      </GridItem>
      <NavigationButtons
        nextHandler={() => nextButtonCb(nextHandler)}
        previousHandler={() => previousButtonCb(previousHandler)}
        disableNext={!currentAnswer}
        disablePrevious={disablePrevious ? 'invisible' : undefined}
        hideNext={removeContinueCtaV1}
      />
    </>
  )
}

export default withFade(EmploymentStatus)
